import { LoadingIndicator } from '@isbj-redesign/shared-components'
// @ts-ignore: correct import https://www.npmjs.com/package/jose https://www.npmjs.com/package/jose-browser-runtime
import compactDecrypt from 'jose-browser-runtime/dist/browser/jwe/compact/decrypt'
import React, { createContext, FC, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

const sessionStorageKey = 'cryptoKey'

type DecryptResult = {
    plaintext: Uint8Array
}
type EncryptionContext = {
    oeffentlicherNutzerSchluesselJwk: JsonWebKey
    decrypt: (data: Uint8Array | string) => Promise<DecryptResult>
    schluesselUuid: string
}
type KeyStore = { cryptoKey: CryptoKeyPair; publicKeyJwk: JsonWebKey; uuid: string }
type EncryptionConfig = {
    name: string
    modulusLength: number
    publicExponent: Uint8Array
    hash: string
}

export const EncryptionProvider: FC = ({ children }) => {
    const config = useEncryptionConfig()
    const [keys, setKeys] = useState<KeyStore>()
    const readKeys = useGetKeys({ config, setKeys })
    const saveToSessionStorage = useSetKeys({ keys })

    useEffect(() => {
        saveToSessionStorage()
    }, [saveToSessionStorage])

    useEffect(() => {
        readKeys()
    }, [readKeys])

    const decrypt = useCallback(
        async (data: Uint8Array | string) => {
            if (!keys) throw new Error('Schlüsselpaar nicht verfügbar')
            return compactDecrypt(data, keys.cryptoKey.privateKey)
        },
        [keys]
    )

    return !keys?.publicKeyJwk ? (
        <LoadingIndicator />
    ) : (
        <Context.Provider
            value={{
                decrypt,
                oeffentlicherNutzerSchluesselJwk: keys.publicKeyJwk,
                schluesselUuid: keys.uuid,
            }}
        >
            {children}
        </Context.Provider>
    )
}

const useSetKeys = ({ keys }: { keys?: KeyStore }) => {
    return useCallback(async () => {
        if (keys?.cryptoKey?.privateKey && keys?.cryptoKey?.publicKey) {
            sessionStorage.setItem(
                sessionStorageKey,
                JSON.stringify({
                    privateKey: await crypto.subtle.exportKey('jwk', keys.cryptoKey.privateKey),
                    publicKey: await crypto.subtle.exportKey('jwk', keys?.cryptoKey?.publicKey),
                    uuid: keys.uuid,
                })
            )
        }
    }, [keys])
}

const useGetKeys = ({ config, setKeys }: { config: EncryptionConfig; setKeys: React.Dispatch<React.SetStateAction<KeyStore | undefined>> }) => {
    return useCallback(async () => {
        const stored = sessionStorage.getItem(sessionStorageKey)
        let readCryptoKey: CryptoKeyPair
        let uuid: string
        if (stored) {
            try {
                const read = JSON.parse(stored)
                readCryptoKey = {
                    privateKey: await crypto.subtle.importKey('jwk', read.privateKey, { name: config.name, hash: { name: config.hash } }, true, ['decrypt']),
                    publicKey: await crypto.subtle.importKey('jwk', read.publicKey, { name: config.name, hash: { name: config.hash } }, true, ['encrypt']),
                }
                uuid = read.uuid
            } catch (e) {
                throw new Error('Schlüsselpaar ist invalide')
            }
        } else {
            readCryptoKey = await crypto.subtle.generateKey(config, true, ['encrypt', 'decrypt'])
            uuid = uuidv4()
        }

        if (!readCryptoKey.privateKey || !readCryptoKey.publicKey) throw new Error('Schlüsselpaar konnte nicht erzeugt weren')
        setKeys({
            cryptoKey: readCryptoKey,
            publicKeyJwk: await crypto.subtle.exportKey('jwk', readCryptoKey.publicKey),
            uuid,
        })
    }, [config, setKeys])
}

const Context = createContext<EncryptionContext | undefined>(undefined)

export const useEncryption = () => {
    const context = useContext(Context)
    if (!context) throw new Error('missing encryption provider!')
    return context
}

const useEncryptionConfig = (): EncryptionConfig => {
    return useMemo(
        () => ({
            name: 'RSA-OAEP',
            modulusLength: 2048,
            publicExponent: new Uint8Array([1, 0, 1]),
            hash: 'SHA-256',
        }),
        []
    )
}
